import React from 'react';

import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router';

import { appRoutes } from '@clearverify/constants';

import { useUserRegistrationVerifierManagerMutation } from 'src/api';
import { Button } from 'src/components/common';
import { useNotification } from 'src/hooks/app';
import { useAppAuth, useAuthSignupVerifier } from 'src/hooks/auth';
import type { SxProp } from 'src/types/mui';

import { prepareFormDataVerifierManager } from '../utils';

export const VerifierYourCompanyExistInfoBlock: React.FC = () => {
  const navigate = useNavigate();

  const { formData } = useAuthSignupVerifier();
  const { setAuthState } = useAppAuth();
  const { createApolloContext } = useNotification();

  const [registerManager, { loading: registerManagerLoading }] =
    useUserRegistrationVerifierManagerMutation();

  const handleSubmit = React.useCallback(async () => {
    try {
      const { data } = await registerManager({
        variables: await prepareFormDataVerifierManager(formData),
        context: createApolloContext({
          errorMessage: 'Something went wrong during manager registration',
        }),
      });
      await setAuthState({
        bearerToken: String(data?.userRegistrationVerifierManager.idToken),
        accessToken: data?.userRegistrationVerifierManager.accessToken ?? undefined,
      });
      navigate(appRoutes.auth.setupMfa);
    } catch (error) {
      console.error(error);
    }
  }, [registerManager, formData, createApolloContext, setAuthState, navigate]);

  return (
    <Box>
      <Typography variant="h5">Your company exists already on Clear Verify</Typography>

      <Typography variant="body1" color="text.secondary">
        It appear that your company has already signed up in Clear Verify. For this reason, we
        cannot accept the Company Information you provided. You can check and edit information as
        needed in the Company Information section under My Account.
      </Typography>

      <Box display="flex" justifyContent="center" mt={4}>
        <Button
          onClick={handleSubmit}
          loading={registerManagerLoading}
          disabled={registerManagerLoading}
          sx={submitButtonStyles}
          size="large"
          variant="contained"
        >
          Go to the dashboard
        </Button>
      </Box>
    </Box>
  );
};

const submitButtonStyles: SxProp = {
  px: 5,
};
